import { useEffect, useMemo } from "react";
import { Title as DialogTitle } from "@radix-ui/react-dialog";
import { FaRocket } from "react-icons/fa";

import { Button } from "components/common/buttons/Button";
import { EmbeddedStudioPurchaseModal } from "components/common/EmbeddedStudioPurchaseModal";
import { Modal, ModalBody } from "components/common/modal";
import { useAnalytics } from "hooks/useAnalytics";
import {
  getWriterToStudioUpgradeCreditDelta,
  getWriterToStudioUpgradeDisplayUsd,
  useOtpProducts,
  useWriterToStudioUpgrade,
} from "hooks/project";
import { ANALYTICS_EVENTS } from "analytics";

type WriterToStudioUpgradeModalProps = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string;
};

export const WriterToStudioUpgradeModal = ({
  open,
  onClose,
  projectId,
  projectName,
}: WriterToStudioUpgradeModalProps) => {
  const { trackEvent } = useAnalytics();
  const { data: otpProducts = [], isFetched } = useOtpProducts(open);
  const upgradePriceDisplay = useMemo(
    () => getWriterToStudioUpgradeDisplayUsd(isFetched ? otpProducts : [], "$100.01"),
    [isFetched, otpProducts]
  );
  const upgradeCreditDelta = useMemo(
    () => getWriterToStudioUpgradeCreditDelta(isFetched ? otpProducts : [], 70),
    [isFetched, otpProducts]
  );

  const {
    startUpgrade,
    isLoading,
    embeddedSession,
    dismissEmbeddedCheckout,
    onEmbeddedPurchaseComplete,
  } = useWriterToStudioUpgrade(projectId);

  useEffect(() => {
    if (!open) return;
    trackEvent(ANALYTICS_EVENTS.TIER_UPGRADE_PROMPT_SHOWN, {
      source: "writer_to_studio_modal",
      project_id: projectId,
    });
  }, [open, projectId, trackEvent]);

  if (!open) {
    return null;
  }

  const name = projectName?.trim() || "this project";

  return (
    <>
      <Modal
        isOpen={open}
        onClose={onClose}
        backdropClassName="bg-black/70 backdrop-blur-md"
        className="bg-transparent border-none shadow-none p-0"
      >
        <ModalBody className="p-0 bg-transparent">
          <div className="flex justify-center items-start sm:items-center min-h-screen py-4 sm:py-6">
            <div
              className="relative w-full max-w-[26rem] overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-card to-background shadow-2xl shadow-black/60 animate-in fade-in-0 zoom-in-95 duration-300"
              role="dialog"
              aria-modal="true"
              aria-labelledby="writer-studio-upgrade-title"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-32 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-hue-sky/20 blur-3xl"
              />

              <Button
                onClick={onClose}
                color="gray"
                size="icon"
                className="absolute top-3 right-3 z-10 h-7 w-7 rounded-full"
                aria-label="Close upgrade dialog"
                type="button"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>

              <div className="relative flex flex-col items-center gap-3 px-6 pt-8 pb-5 text-center">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 rounded-2xl bg-hue-sky/20 blur-xl"
                  />
                  <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-hue-sky/25 bg-hue-sky/10 text-hue-sky">
                    <FaRocket className="-rotate-45 translate-y-px text-xl" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <DialogTitle
                    id="writer-studio-upgrade-title"
                    className="text-lg font-semibold leading-tight text-foreground sm:text-xl"
                  >
                    Upgrade to Studio
                  </DialogTitle>
                  <p className="text-sm leading-snug text-muted-foreground">
                    You currently have a{" "}
                    <span className="font-medium text-foreground/80">Writer</span> license on{" "}
                    <span className="font-medium text-primary">{name}</span>. Upgrade to Studio for{" "}
                    {upgradePriceDisplay} more to unlock AI scheduling & budgeting, larger credit
                    pool, and more seats.
                  </p>
                </div>
              </div>

              <div className="border-t border-border bg-muted/60 px-6 py-5">
                <div className="mb-4 flex flex-col gap-1 text-left text-xs text-muted-foreground">
                  <span>
                    Includes{" "}
                    <span className="font-medium text-foreground/80">
                      +{upgradeCreditDelta} AI credits
                    </span>{" "}
                    for this project (Studio bundle vs Writer).
                  </span>
                  <span>Checkout is secure via Stripe.</span>
                </div>
                <Button
                  type="button"
                  onClick={() => void startUpgrade()}
                  isLoading={isLoading}
                  className="h-11 w-full font-semibold"
                >
                  Continue to checkout ({upgradePriceDisplay})
                </Button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
      {embeddedSession ? (
        <EmbeddedStudioPurchaseModal
          isOpen
          clientSecret={embeddedSession.clientSecret}
          publishableKey={embeddedSession.publishableKey}
          onClose={dismissEmbeddedCheckout}
          onComplete={() => {
            void onEmbeddedPurchaseComplete();
            onClose();
          }}
        />
      ) : null}
    </>
  );
};
